import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import PageHeader from "@/components/common/PageHeader";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { EmailVerificationBanner } from "@/components/affiliate/EmailVerificationBanner";
import { useAffiliate } from "@/contexts/affiliate/hooks";
import { PersonalInfo } from "./tabs/PersonalInfo";
import { ProfileDetails } from "./tabs/ProfileDetails";
import { PaymentInfo } from "./tabs/PaymentInfo";

type SettingsTab = "personal" | "profile" | "payment";

// Tab ids are tour anchors (#personal, #profile, #payment) and deep links.
const TABS: { id: SettingsTab; label: string }[] = [
  { id: "personal", label: "My details" },
  { id: "profile", label: "Profile" },
  { id: "payment", label: "Payment methods" },
];

function tabFrom(params: URLSearchParams): SettingsTab {
  const tab = params.get("tab");
  return TABS.some((t) => t.id === tab) ? (tab as SettingsTab) : "personal";
}

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { details } = useAffiliate();
  const [verified, setVerified] = useState(false);
  const tab = tabFrom(searchParams);
  const supportedPaymentMethods = searchParams.get("supportedPaymentMethods")?.split(",").filter(Boolean) ?? [];

  useEffect(() => {
    toast.dismiss();
  }, [tab]);

  const setTab = (next: SettingsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    navigate({ search: params.toString() }, { replace: true });
  };

  const showBanner = Boolean(details) && (!details!.isEmailVerified || verified);

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" />

      {showBanner && details && (
        <EmailVerificationBanner
          affiliateId={details.id}
          email={details.email}
          status={details.isEmailVerified || verified ? "success" : "pending"}
          onVerified={() => setVerified(true)}
        />
      )}

      <UnderlineTabs<SettingsTab>
        tabs={TABS.map((t) => ({ value: t.id, label: t.label, id: t.id }))}
        value={tab}
        onValueChange={setTab}
        accent="brand"
      />

      {tab === "personal" && <PersonalInfo />}
      {tab === "profile" && <ProfileDetails />}
      {tab === "payment" && <PaymentInfo supportedPaymentMethods={supportedPaymentMethods} />}
    </div>
  );
}
